/**
 * Voice recording in the browser.
 *
 * MediaRecorder's output format is not negotiable across browsers — Chrome and
 * Firefox give WebM/Opus, Safari gives MP4/AAC. Rather than fight it we record
 * in whatever the browser offers and carry the real mime type on the
 * attachment, so the recipient's <audio> element gets something it can play.
 */
import { computeWaveform, encodeWaveform, WAVEFORM_BUCKETS } from '@vav/protocol';

/** In preference order; the first the browser admits to supporting wins. */
const CANDIDATE_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
];

export interface Recording {
  bytes: Uint8Array;
  mime: string;
  durationMs: number;
  /** Base64 peaks, ready to attach. */
  waveform: string;
}

export function recordingSupported(): boolean {
  return (
    typeof MediaRecorder !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    Boolean(navigator.mediaDevices?.getUserMedia)
  );
}

function pickMimeType(): string | undefined {
  return CANDIDATE_TYPES.find((type) => MediaRecorder.isTypeSupported(type));
}

export type MicPermission = 'granted' | 'denied' | 'prompt' | 'unknown';

/**
 * What will happen if the microphone is asked for right now.
 *
 * Worth knowing before asking, because the three answers need three different
 * things from the UI. `prompt` means pressing the button raises the browser's
 * own dialog and the user simply approves — which is what everyone expects to
 * happen and what does happen, once. `denied` means it will not: a refused
 * permission cannot be re-requested by the page, by design, and no amount of
 * asking again changes that. Only site settings do.
 *
 * `unknown` covers browsers whose Permissions API does not list a microphone
 * (Firefox, historically). There the only way to find out is to try.
 */
export async function micPermission(): Promise<MicPermission> {
  if (!navigator.permissions?.query) return 'unknown';
  try {
    const status = await navigator.permissions.query({
      name: 'microphone' as PermissionName,
    });
    return status.state;
  } catch {
    return 'unknown';
  }
}

/**
 * Call `onChange` when the microphone permission is granted or revoked.
 *
 * So that fixing it in site settings takes effect here immediately. Without
 * this, someone follows the instructions, comes back to a screen still saying
 * the microphone is blocked, and concludes the instructions did not work.
 */
export function watchMicPermission(onChange: (state: MicPermission) => void): () => void {
  let status: PermissionStatus | undefined;
  let cancelled = false;

  void (async () => {
    if (!navigator.permissions?.query) return;
    try {
      const result = await navigator.permissions.query({ name: 'microphone' as PermissionName });
      if (cancelled) return;
      status = result;
      result.addEventListener('change', () => onChange(result.state));
    } catch {
      // No microphone entry in this browser's Permissions API. Nothing to watch.
    }
  })();

  return () => {
    cancelled = true;
    if (status) status.onchange = null;
  };
}

export class VoiceRecorder {
  #recorder?: MediaRecorder;
  #stream?: MediaStream;
  #chunks: BlobPart[] = [];
  #startedAt = 0;
  #mime = '';

  get active(): boolean {
    return this.#recorder?.state === 'recording';
  }

  get elapsedMs(): number {
    return this.#startedAt ? Date.now() - this.#startedAt : 0;
  }

  /** Throws if the user denies microphone access. */
  async start(): Promise<void> {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        // Voice, not music: let the browser do the work a phone call would.
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
    this.#stream = stream;
    this.#mime = pickMimeType() ?? '';
    this.#chunks = [];

    const recorder = new MediaRecorder(stream, this.#mime ? { mimeType: this.#mime } : undefined);
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) this.#chunks.push(event.data);
    };
    this.#recorder = recorder;
    this.#startedAt = Date.now();
    recorder.start();
  }

  /** Stop and discard. Used when the user cancels mid-recording. */
  cancel(): void {
    this.#teardown();
  }

  async stop(): Promise<Recording | undefined> {
    const recorder = this.#recorder;
    if (!recorder || recorder.state === 'inactive') {
      this.#teardown();
      return undefined;
    }

    const durationMs = this.elapsedMs;
    const finished = new Promise<void>((resolve) => {
      recorder.onstop = () => resolve();
    });
    recorder.stop();
    await finished;

    const mime = recorder.mimeType || this.#mime || 'audio/webm';
    const blob = new Blob(this.#chunks, { type: mime });
    this.#teardown();

    // Too short to be intentional — usually a mis-tap on the mic button.
    if (durationMs < 350 || blob.size === 0) return undefined;

    const bytes = new Uint8Array(await blob.arrayBuffer());
    return { bytes, mime, durationMs, waveform: await waveformFor(bytes, mime) };
  }

  #teardown(): void {
    this.#recorder = undefined;
    this.#chunks = [];
    this.#startedAt = 0;
    // Releases the microphone, and with it the browser's recording indicator.
    this.#stream?.getTracks().forEach((track) => track.stop());
    this.#stream = undefined;
  }
}

/**
 * Decode the clip and reduce it to drawable peaks.
 *
 * Falls back to a flat waveform rather than failing the send: a missing
 * decoder is no reason to lose the recording.
 */
async function waveformFor(bytes: Uint8Array, mime: string): Promise<string> {
  try {
    const AudioCtx: typeof AudioContext =
      window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const context = new AudioCtx();
    try {
      const buffer = await context.decodeAudioData(bytes.slice().buffer as ArrayBuffer);
      return encodeWaveform(computeWaveform(buffer.getChannelData(0)));
    } finally {
      void context.close();
    }
  } catch {
    void mime;
    return encodeWaveform(new Uint8Array(WAVEFORM_BUCKETS).fill(70));
  }
}
